import React, { Fragment } from "react";
import { Menu, Transition } from "@headlessui/react";
import { Link } from "react-router-dom";
import Avatar from "react-avatar";
import classnames from "classnames";
import { ChevronDownIcon, MenuIcon, XIcon, CollectionIcon, LightBulbIcon, UsersIcon } from "@heroicons/react/outline";
import { ReactComponent as HelpIcon } from "assets/images/help.svg";
import { ReactComponent as NotificationIcon } from "assets/images/notification.svg";
import { IUser } from "types";

// Editorial Logo Component - matches landing page style exactly
export function EditorialLogo() {
  return (
    <a href="/" className="inline-flex items-baseline">
      <span
        className="text-2xl font-semibold"
        style={{
          color: '#1C1917',
          fontFamily: "'Fraunces', serif"
        }}
      >
        WE
      </span>
      <span
        className="text-2xl font-light italic"
        style={{
          color: '#44403C',
          fontFamily: "'Fraunces', serif"
        }}
      >
        attitude
      </span>
      <span className="text-xs font-medium opacity-50">.org</span>
    </a>
  );
}

export interface NavItem {
  name: string;
  href: string;
  current: boolean;
}

export interface ProfileMenuItem {
  name: string;
  href: string;
  action?: "logout";
}

interface GlobalHeaderProps {
  className?: string;
  primaryNav: NavItem[];
  moreNav: NavItem[];
  profileMenu: ProfileMenuItem[];
  isAuth?: boolean;
  profile?: IUser;
  isFetchingProfile?: boolean;
  avatarColor: string;
  notificationsCount: number;
  highlightProfile?: boolean;
  onLogoClick: () => void;
  onHelpClick: () => void;
  helpContent?: React.ReactNode;
  onNotificationsClick: () => void;
  notificationContent?: React.ReactNode;
  onSignInClick: () => void;
  onSignUpClick: () => void;
  onLogout: () => void;
  onMobileMenuToggle: () => void;
  isMobileMenuOpen: boolean;
  showMobileMenuToggle?: boolean;
  isHelpActive?: boolean;
  isNotificationsActive?: boolean;
}

function GlobalHeader({
  className,
  primaryNav,
  moreNav,
  profileMenu,
  isAuth,
  profile,
  isFetchingProfile,
  avatarColor,
  notificationsCount,
  highlightProfile,
  onLogoClick,
  onHelpClick,
  helpContent,
  onNotificationsClick,
  notificationContent,
  onSignInClick,
  onSignUpClick,
  onLogout,
  onMobileMenuToggle,
  isMobileMenuOpen,
  showMobileMenuToggle = true,
  isHelpActive = false,
  isNotificationsActive = false,
}: GlobalHeaderProps) {
  const showMoreMenu = moreNav.length > 0;
  const moreIsActive = moreNav.some((item) => item.current);
  const profileName =
    `${profile?.firstName ?? ""} ${profile?.lastName ?? ""}`.trim() ||
    profile?.email ||
    "User";

  const iconMap: Record<string, React.ComponentType<React.SVGProps<SVGSVGElement>>> = {
    Issues: CollectionIcon,
    Solutions: LightBulbIcon,
    "Action Groups": UsersIcon,
  };

  return (
    <div
      className={classnames("relative z-30 border-b", className)}
      style={{
        borderColor: '#E7E5E4',
        fontFamily: "'Source Sans 3', sans-serif"
      }}
    >
      <div className="flex h-16 w-full items-center gap-4 px-4 sm:px-6 lg:px-8">
        {showMobileMenuToggle && (
          <button
            type="button"
            onClick={onMobileMenuToggle}
            className="inline-flex h-10 w-10 items-center justify-center rounded-lg transition-colors lg:hidden"
            style={{
              border: '1px solid #E7E5E4',
              color: '#44403C',
            }}
          >
            <span className="sr-only">
              {isMobileMenuOpen ? "Close navigation" : "Open navigation"}
            </span>
            {isMobileMenuOpen ? (
              <XIcon className="h-5 w-5" aria-hidden="true" />
            ) : (
              <MenuIcon className="h-5 w-5" aria-hidden="true" />
            )}
          </button>
        )}

        <div className="flex items-center" onClick={onLogoClick} style={{ cursor: 'pointer' }}>
          <EditorialLogo />
        </div>

        <div className="hidden flex-1 justify-center lg:flex">
          <div className="flex items-center gap-1">
            {primaryNav.map((item) => {
              const Icon = iconMap[item.name];
              return (
                <Link
                  key={item.name}
                  to={item.href}
                  className="group relative inline-flex items-center gap-2 px-4 py-2 text-sm font-medium transition-all"
                  style={{
                    fontFamily: "'Source Sans 3', sans-serif",
                    color: item.current ? '#C2410C' : '#44403C',
                  }}
                  onMouseEnter={(e) => {
                    if (!item.current) {
                      e.currentTarget.style.color = '#C2410C';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (!item.current) {
                      e.currentTarget.style.color = '#44403C';
                    }
                  }}
                >
                  {Icon && (
                    <Icon
                      className="h-4 w-4"
                      style={{
                        color: item.current ? '#C2410C' : '#78716C'
                      }}
                    />
                  )}
                  <span className="whitespace-nowrap">{item.name}</span>
                  {/* Underline indicator for active state */}
                  <span
                    className="absolute bottom-0 left-1/2 -translate-x-1/2 h-0.5 rounded-full transition-all duration-200"
                    style={{
                      width: item.current ? '60%' : '0%',
                      background: '#C2410C',
                    }}
                  />
                </Link>
              );
            })}
            {showMoreMenu && (
              <>
                <span
                  className="mx-1 hidden h-5 w-px rounded-full lg:block"
                  style={{ background: '#E7E5E4' }}
                  aria-hidden="true"
                />
                <Menu as="div" className="relative">
                  <Menu.Button
                    className="relative inline-flex items-center gap-1.5 px-3 py-2 text-sm font-medium transition-all focus:outline-none"
                    style={{
                      fontFamily: "'Source Sans 3', sans-serif",
                      color: moreIsActive ? '#C2410C' : '#44403C',
                    }}
                  >
                    <span>More</span>
                    <ChevronDownIcon className="h-4 w-4" aria-hidden="true" />
                    {/* Underline indicator for active state */}
                    <span
                      className="absolute bottom-0 left-1/2 -translate-x-1/2 h-0.5 rounded-full transition-all duration-200"
                      style={{
                        width: moreIsActive ? '60%' : '0%',
                        background: '#C2410C',
                      }}
                    />
                  </Menu.Button>
                <Transition
                  as={Fragment}
                  enter="transition ease-out duration-100"
                  enterFrom="transform opacity-0 scale-95"
                  enterTo="transform opacity-100 scale-100"
                  leave="transition ease-in duration-75"
                  leaveFrom="transform opacity-100 scale-100"
                  leaveTo="transform opacity-0 scale-95"
                >
                  <Menu.Items
                    className="absolute left-0 z-20 mt-2 w-56 rounded-xl py-2 shadow-lg focus:outline-none"
                    style={{
                      background: '#FFFEF9',
                      border: '1px solid #E7E5E4'
                    }}
                  >
                    {moreNav.map((item) => (
                      <Menu.Item key={item.name}>
                        {({ active }) => (
                          <Link
                            to={item.href}
                            className="block px-4 py-2.5 text-sm transition-colors"
                            style={{
                              fontFamily: "'Source Sans 3', sans-serif",
                              background: active ? 'rgba(194, 65, 12, 0.06)' : (item.current ? 'rgba(194, 65, 12, 0.1)' : 'transparent'),
                              color: active || item.current ? '#C2410C' : '#44403C'
                            }}
                          >
                            {item.name}
                          </Link>
                        )}
                      </Menu.Item>
                    ))}
                  </Menu.Items>
                </Transition>
                </Menu>
              </>
            )}
          </div>
        </div>

        <div className="ml-auto flex items-center gap-3">
          <div className="relative">
            <button
              type="button"
              onClick={onHelpClick}
              className="rounded-full p-2 transition-colors focus:outline-none"
              style={{
                color: isHelpActive ? '#C2410C' : '#78716C',
                background: isHelpActive ? 'rgba(194, 65, 12, 0.08)' : 'transparent'
              }}
            >
              <span className="sr-only">Help</span>
              <HelpIcon className="h-5 w-5" aria-hidden="true" />
            </button>
            {helpContent}
          </div>

          {isAuth ? (
            <>
              <div className="relative">
                <button
                  type="button"
                  onClick={onNotificationsClick}
                  className="relative rounded-full p-2 transition-colors focus:outline-none"
                  style={{
                    color: isNotificationsActive ? '#C2410C' : '#78716C',
                    background: isNotificationsActive ? 'rgba(194, 65, 12, 0.08)' : 'transparent'
                  }}
                >
                  <span className="sr-only">Notifications</span>
                  {notificationsCount > 0 && (
                    <span
                      className="absolute -top-1 -right-1 inline-flex min-h-[1.25rem] min-w-[1.25rem] items-center justify-center rounded-full px-1 text-xs font-semibold text-white"
                      style={{ background: '#C2410C' }}
                    >
                      {notificationsCount > 99 ? "99+" : notificationsCount}
                    </span>
                  )}
                  <NotificationIcon className="h-5 w-5" aria-hidden="true" />
                </button>
                {notificationContent}
              </div>

              <Menu as="div" className="relative">
                <Menu.Button
                  className={classnames(
                    "flex h-10 w-10 items-center justify-center rounded-full border-2 focus:outline-none transition-all",
                    {
                      "ring-4 ring-offset-2": highlightProfile,
                      "ring-[rgba(194,65,12,0.3)]": highlightProfile,
                    }
                  )}
                  style={{
                    borderColor: highlightProfile ? '#C2410C' : '#E7E5E4',
                  }}
                >
                  <span className="sr-only">Open user menu</span>
                  <div className="h-9 w-9 overflow-hidden rounded-full bg-stone-100">
                    {profile?.avatar && !isFetchingProfile ? (
                      <img
                        src={profile.avatar}
                        alt=""
                        className="h-full w-full object-cover"
                      />
                    ) : (
                      <Avatar
                        size="36"
                        round
                        color={avatarColor}
                        name={profileName}
                      />
                    )}
                  </div>
                </Menu.Button>
                <Transition
                  as={Fragment}
                  enter="transition ease-out duration-100"
                  enterFrom="transform opacity-0 scale-95"
                  enterTo="transform opacity-100 scale-100"
                  leave="transition ease-in duration-75"
                  leaveFrom="transform opacity-100 scale-100"
                  leaveTo="transform opacity-0 scale-95"
                >
                  <Menu.Items
                    className="absolute right-0 z-20 mt-2 w-48 rounded-xl py-2 shadow-lg focus:outline-none"
                    style={{
                      background: '#FFFEF9',
                      border: '1px solid #E7E5E4'
                    }}
                  >
                    {profileMenu.map((item) => (
                      <Menu.Item key={item.name}>
                        {({ active }) =>
                          item.action === "logout" ? (
                            <button
                              type="button"
                              onClick={onLogout}
                              className="block w-full px-4 py-2.5 text-left text-sm transition-colors"
                              style={{
                                fontFamily: "'Source Sans 3', sans-serif",
                                background: active ? 'rgba(194, 65, 12, 0.06)' : 'transparent',
                                color: active ? '#C2410C' : '#44403C'
                              }}
                            >
                              {item.name}
                            </button>
                          ) : (
                            <Link
                              to={item.href}
                              className="block px-4 py-2.5 text-sm transition-colors"
                              style={{
                                fontFamily: "'Source Sans 3', sans-serif",
                                background: active ? 'rgba(194, 65, 12, 0.06)' : 'transparent',
                                color: active ? '#C2410C' : '#44403C'
                              }}
                            >
                              {item.name}
                            </Link>
                          )
                        }
                      </Menu.Item>
                    ))}
                  </Menu.Items>
                </Transition>
              </Menu>
            </>
          ) : (
            <button
              type="button"
              onClick={onSignInClick}
              className="hidden sm:block text-sm font-semibold px-5 py-2 rounded-lg transition-all text-white"
              style={{
                fontFamily: "'Source Sans 3', sans-serif",
                background: '#1C1917',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'scale(1.05)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'scale(1)';
              }}
            >
              Get Started
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

export default GlobalHeader;
